import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import UnauthedHeader from '@components/UnauthedHeader';
import { actions as accountActions } from '@reducers/Account';
import { actions as loginActions } from '@reducers/login';
import { isLoadingAccountSelector } from 'selectors/Account';
import OAuthButton from '@components/OAuthButton';
import {
  displayNameForProvider,
  isCloudProvider,
} from '@utils/providerHelpers';
import queryString from 'qs';
import { Loading } from 'Common/components/Atoms/Loading';
import { Button, Div, Card, Flex, InputField, Link, Modal } from 'Common';
import { Text } from 'Common/components/Atoms/Text';
import {
  USER_LOGIN_EMAIL_CLICK,
  USER_LOGIN_OAUTH_GITHUB_CLICK,
  USER_LOGIN_OAUTH_GITLAB_CLICK,
  USER_LOGIN_OAUTH_AZURE_DEVOPS_CLICK,
  USER_LOGIN_OAUTH_BITBUCKET_CLICK,
  USER_LOGIN_OAUTH_HACKERONE_CLICK,
} from '@utils/analytics';
import { LOGIN_CLICK_EVENT } from '@utils/storageHelpers';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Email } from 'constants/email';
import { mail } from 'fluent-mailto';
import { generators } from 'stores';
import { isOn } from 'utils/featureFlags/global/isOn';
import { GlobalFeatureFlag } from 'constants/FeatureFlags';

const useStyles = makeStyles((theme) => ({
  line: {
    flex: '1 1',
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
  },
}));

function Login(props) {
  const { isLoading, isLoggingIn, redirectTo2fa, fetchAccount } = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();

  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    /* Call fetchAccount in case someone is reloading the login page.
     * This will automatically redirect a logged-in user. */
    fetchAccount();
    setHasFetched(true);
  }, [fetchAccount]);

  const qs = queryString.parse(props.location.search, {
    ignoreQueryPrefix: true,
  });
  const providerQuery = qs.provider;
  const [values, setValues] = useState({
    email: '',
    password: '',
    isLoading: null,
    passwordReset: qs.password_reset === 'true',
    oauthError: qs.oauth_error,
    installId: qs.install_id,
    provider: qs.provider
      ? displayNameForProvider[qs.provider.toLowerCase()]
      : null,
  });

  const signInLoading = (type) => {
    setValues({ ...values, isLoading: type });
  };
  // TODO: Move redirect to login saga
  if (redirectTo2fa) {
    return <Redirect to="/login/verify" />;
  } else if (!hasFetched || isLoading) {
    // hasFetched gets set to true once we have done the initial account fetch. Otherwise we will
    // return null here so that we don't "blip" in the login page for a frame before starting the fetch.
    return null;
  }

  if (!isLoggingIn && values.isLoading === 'signin') {
    signInLoading(null);
  } else if (isLoggingIn && values.isLoading === null) {
    signInLoading('signin');
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    login(values.email, values.password);
  };
  const handleChange = (name) => (event) => {
    setValues({ ...values, [name]: event.target.value });
  };
  const getSignupLink = () => {
    let uri = '/signup';
    const { location } = props;
    const ref = queryString.parse(location.search.substring(1)).ref;
    if (ref) {
      uri += `?ref=${ref}`;
    }
    return uri;
  };

  const { login, error } = props;
  const errorMessage = error || qs.error_message;
  let message = errorMessage;
  if (!message && values.passwordReset) {
    message = 'Your password was reset successfully!';
  }

  const handleLoginClick = () => {
    localStorage.setItem(LOGIN_CLICK_EVENT, USER_LOGIN_EMAIL_CLICK);
  };

  const handleOauthClick = (provider) => {
    let event;
    switch (provider) {
      case 'hackerone':
        event = USER_LOGIN_OAUTH_HACKERONE_CLICK;
        break;
      case 'azuredevops':
        event = USER_LOGIN_OAUTH_AZURE_DEVOPS_CLICK;
        break;
      case 'gitlab':
        event = USER_LOGIN_OAUTH_GITLAB_CLICK;
        break;
      case 'bitbucket':
        event = USER_LOGIN_OAUTH_BITBUCKET_CLICK;
        break;
      case 'github':
      default:
        event = USER_LOGIN_OAUTH_GITHUB_CLICK;
    }
    localStorage.setItem(LOGIN_CLICK_EVENT, event);
    signInLoading(provider);
    dispatch(
      generators.oauth.handleOauthRedirect.request({
        provider,
      })
    );
  };

  const getOAuthErrorMsg = () => {
    switch (values.oauthError) {
      case 'unverified':
        return (
          <React.Fragment>
            Please make sure that the
            {' ' + values.provider + ' '}
            account you are attempting to connect with has a verified email.
          </React.Fragment>
        );
      case 'permissions':
        return (
          <React.Fragment>
            We were unable to log you in with{' ' + values.provider}, please
            make sure that the application permissions are up to date{' '}
            {values.installId && (
              <Link
                href={`https://github.com/settings/installations/${values.installId}/permissions/update`}
                rel="noreferrer noopener"
              >
                here
              </Link>
            )}{' '}
            or log in with your email address instead.
          </React.Fragment>
        );
      default:
        return (
          <React.Fragment>
            Something went wrong while attempting to login through OAuth.
          </React.Fragment>
        );
    }
  };
  if (providerQuery && isCloudProvider(providerQuery)) {
    return (
      <Div height="100vh" width="100vw" style={{ overflowY: 'auto' }}>
        <UnauthedHeader />
        <Text variant="h1" textAlign="center" mt={10} mb={4}>
          Sign in with {displayNameForProvider(providerQuery)} to continue
        </Text>
        <Card display="block" width={500} m="auto" px={5} py={4}>
          <OAuthButton
            provider={providerQuery}
            onClick={handleOauthClick}
            isLoading={values.isLoading}
          />
          <Text variant="body2" ml={1}>
            {"By signing in, you're agreeing to the "}
            <Link href="https://www.pullrequest.com/terms" target="_blank">
              Terms of Use
            </Link>
            {' and '}
            <Link href="https://www.pullrequest.com/privacy" target="_blank">
              Privacy Policy
            </Link>
            {'.'}
          </Text>
        </Card>
      </Div>
    );
  }
  return (
    <Div height="100vh" width="100vw" style={{ overflowY: 'auto' }}>
      <UnauthedHeader />
      <Text
        variant="h1"
        color={theme.palette.primary.main}
        textAlign="center"
        mt={10}
        mb={4}
      >
        Sign in to PullRequest
      </Text>
      <Card display="block" width={400} m="auto" px={5} py={4} mb={6}>
        {/*Oauth Logins*/}
        <OAuthButton
          provider="github"
          onClick={handleOauthClick}
          isLoading={values.isLoading}
        />
        <OAuthButton
          provider="gitlab"
          onClick={handleOauthClick}
          isLoading={values.isLoading}
        />
        <OAuthButton
          provider="bitbucket"
          onClick={handleOauthClick}
          isLoading={values.isLoading}
        />
        <OAuthButton
          provider="azuredevops"
          onClick={handleOauthClick}
          isLoading={values.isLoading}
        />
        {isOn(
          GlobalFeatureFlag.CLIENT_DISABLE_HACKERONE_OAUTH_LOGIN
        ) ? undefined : (
          <OAuthButton
            provider="hackerone"
            onClick={handleOauthClick}
            isLoading={values.isLoading}
          />
        )}
        <Flex width="100%" alignItems="center" justifyContent="center">
          <div className={classes.line} />
          <Text
            variant="h3"
            fontWeight={theme.typography.fontWeightRegular}
            mb={2}
            color="textSecondary"
            mx={2}
          >
            or
          </Text>
          <div className={classes.line} />
        </Flex>

        {message && (
          <Text
            variant="body1"
            color={errorMessage ? 'error' : 'success'}
            mt={1}
            mb={3}
            align="center"
            minHeight={20}
            width="100%"
          >
            {message}
          </Text>
        )}
        <form onSubmit={handleSubmit}>
          <InputField
            id="email"
            mb={2}
            fullWidth
            placeholder="Email"
            required={true}
            type="email"
            value={values.email}
            onChange={handleChange('email')}
          />
          <InputField
            id="password"
            mb={3}
            fullWidth
            placeholder="Password"
            required={true}
            type="password"
            value={values.password}
            onChange={handleChange('password')}
          />
          <Link
            variant="body2"
            color={theme.palette.secondary.main}
            href="/forgot-password"
          >
            Forgot your password?
          </Link>
          <Button
            mt={3}
            width="100%"
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleLoginClick}
          >
            {values.isLoading === 'signin' ? (
              <Flex center justifyContent="center">
                <Loading customcolor={theme.palette.common.white} size={24} />
              </Flex>
            ) : (
              <React.Fragment>Sign In</React.Fragment>
            )}
          </Button>
        </form>

        <Flex
          center
          justifyContent="center"
          mt={3}
          borderTop={`solid 1px ${theme.palette.grey[300]}`}
        >
          <Text
            variant="body1"
            fontWeight={theme.typography.fontWeightLight}
            color={theme.palette.text.secondary}
            mt={1}
            mb={-2}
            p={1}
          >
            New to PullRequest?{' '}
            <Link color={theme.palette.secondary.main} href={getSignupLink()}>
              Sign Up
            </Link>
          </Text>
        </Flex>
      </Card>
      <Modal
        variant="error"
        width={600}
        isOpen={!!values.oauthError}
        title="Something went wrong"
        onRequestClose={() =>
          setValues({
            email: '',
            password: '',
            isLoading: null,
            oauthError: '',
          })
        }
      >
        {getOAuthErrorMsg()}
        <br />
        <br />
        If the problem persists, contact{' '}
        <Link href={mail.to(Email.SUPPORT).build()}>{Email.SUPPORT}</Link>.
      </Modal>
    </Div>
  );
}

Login.propTypes = {
  error: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isLoggingIn: PropTypes.bool.isRequired,
  login: PropTypes.func.isRequired,
  redirectTo2fa: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  fetchAccount: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const { login } = state;
  const isLoading = isLoadingAccountSelector(state);
  return {
    isLoading,
    isLoggingIn: login.isLoading,
    error: login.error,
    redirectTo2fa: !!login.twoFactorChallengeToken,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...accountActions, ...loginActions }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Login);
